import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tooltip } from '@fluentui/react-components';
import { QuestionCircle20Regular } from '@fluentui/react-icons';

import HelpDocumentationDrawerComponent from './HelpDocumentationDrawer.Component';

import './HelpDocumentation.Styles.css';

export interface HelpDocumentationLinkProps {
    /**
     * Código de enlace con el que la API resuelve la página. Lo emite el sistema de
     * documentación, no esta pantalla: el contenido puede mudarse de proveedor o de
     * página sin que el front se entere.
     */
    linkCode: string;
    /** Texto del disparador. Por defecto, "Ver ayuda". */
    label?: string;
    /**
     * Sólo el ícono, con el texto en un globo.
     *
     * Para donde no entra una frase —una barra de herramientas, el encabezado de una
     * tarjeta, una celda— y la alternativa real no es un disparador más corto sino
     * ninguno. El texto no se pierde: sigue siendo el nombre accesible del botón, así
     * que un lector de pantalla anuncia lo mismo en los dos modos.
     */
    iconOnly?: boolean;
    /** Encabezado del panel mientras la página no llegó con el suyo. */
    drawerTitle?: string;
    appearance?: 'subtle' | 'transparent' | 'secondary' | 'primary' | 'outline';
    size?: 'small' | 'medium' | 'large';
    className?: string;
}

/**
 * Disparador de la ayuda en contexto: el botón y el panel juntos, para que una
 * pantalla que quiera ofrecer ayuda ponga un solo componente y no tenga que
 * manejar el estado de apertura por su cuenta.
 *
 * El panel se monta recién cuando se abre por primera vez. Son pantallas con
 * muchos de estos disparadores, y ninguno tiene por qué costar nada hasta que
 * alguien lo usa.
 */
const HelpDocumentationLinkComponent: React.FC<HelpDocumentationLinkProps> = ({
    linkCode,
    label,
    iconOnly = false,
    drawerTitle,
    appearance = 'subtle',
    size = 'small',
    className,
}) => {
    const [t] = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [wasOpened, setWasOpened] = useState<boolean>(false);

    const open = () => {
        setWasOpened(true);
        setIsOpen(true);
    };

    const text = label ?? String(t('helpDocumentation.open'));

    /* Las apariencias con relleno propio se visten solas. El tratamiento silencioso
       —fondo transparente, pastilla recién al pasar por encima— es para las otras,
       y se marca acá y no en la hoja de estilos porque Fluent no deja una clase
       estable que diga qué apariencia se pidió. */
    const isSolid = appearance === 'primary' || appearance === 'secondary';

    const trigger = (
        <Button
            appearance={appearance}
            size={size}
            className={
                'help-doc__trigger' +
                (isSolid ? ' help-doc__trigger--solid' : ' help-doc__trigger--quiet') +
                (iconOnly ? ' help-doc__trigger--iconOnly' : '') +
                (isOpen && !isSolid ? ' help-doc__trigger--open' : '') +
                (className ? ' ' + className : '')
            }
            icon={<QuestionCircle20Regular />}
            onClick={open}
            /* Lo que abre es un panel modal, no una pantalla ni un menú. Sin esto un
               lector de pantalla anuncia un botón cualquiera y nada indica que el foco
               está por irse a otro lado, ni —cuando ya está abierto— que lo que se ve
               al costado salió de acá. */
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-label={iconOnly ? text : undefined}
        >
            {iconOnly ? undefined : text}
        </Button>
    );

    return (
        <>
            {/* El globo sólo donde el texto no está a la vista. Sobre un botón que ya
                dice lo que hace, repetirlo tapa lo que el lector estaba mirando. */}
            {iconOnly ? (
                <Tooltip content={text} relationship="label" withArrow>
                    {trigger}
                </Tooltip>
            ) : (
                trigger
            )}

            {wasOpened && (
                <HelpDocumentationDrawerComponent
                    linkCode={linkCode}
                    isOpen={isOpen}
                    onDismiss={() => setIsOpen(false)}
                    fallbackTitle={drawerTitle}
                />
            )}
        </>
    );
};

export default HelpDocumentationLinkComponent;
